import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Pagination,
} from "react-bootstrap";

import api, {
  getPendampingFileUrl,
  resolvePendampingDocUrl,
  updatePendampingReferral,
} from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import {
  generateKartuPengenalImage,
  downloadDataUrl,
  printDataUrl,
  KARTU_FORMAT,
  getKartuFormatLabel,
} from "../../utils/generateKartuPengenal";
import "./Dashboard.css";
import "./ProfilPendamping.css";

const PAGE_SIZE = 20;

const getDocFileUrl = (folder, filename) => getPendampingFileUrl(folder, filename);

const DaftarPendamping = () => {
  const { orgName, orgAlamat, orgTelepon, logoUrl, themeColor, themeDark, themeLight } =
    useTheme();
  const [pendampingList, setPendampingList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevSearchRef = useRef("");
  const [selectedPendamping, setSelectedPendamping] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("DISETUJUI");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncingRows, setSyncingRows] = useState({});

  // Reset password state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(null); // 'unik_id' | 'id' | null
  const [resetResult, setResetResult] = useState(null);

  // Ganti Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [ptspData, setPtspData] = useState({
    username: "",
    email: "",
    phone_no: "",
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailFetchLoading, setEmailFetchLoading] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const [showKartuModal, setShowKartuModal] = useState(false);
  const [kartuPreviewUrl, setKartuPreviewUrl] = useState("");
  const [kartuLoading, setKartuLoading] = useState(false);
  const [kartuFormat, setKartuFormat] = useState(KARTU_FORMAT.HORIZONTAL);
  const [kartuError, setKartuError] = useState(null);

  const [referralForm, setReferralForm] = useState({
    kode_referral: "",
    referral: "",
  });
  const [referralSaving, setReferralSaving] = useState(false);
  const [referralMessage, setReferralMessage] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (prevSearchRef.current === trimmed) return;
      prevSearchRef.current = trimmed;
      setDebouncedSearch(trimmed);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchPendampingData(filterStatus, currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, currentPage, debouncedSearch]);

  const fetchPendampingData = async (
    status = filterStatus,
    page = currentPage,
    search = debouncedSearch,
  ) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, page_size: PAGE_SIZE };
      if (status !== "ALL") params.status = status;
      if (search) params.q = search;
      const response = await api.get("/pendamping/external", { params });
      if (response.data.success) {
        const list = response.data.data || [];
        const total =
          response.data.pagination?.total ??
          response.data.total ??
          (list.length < PAGE_SIZE && page === 1 ? list.length : 0);
        setPendampingList(list);
        setFilteredList(list);
        setTotalRecords(
          total > 0
            ? total
            : list.length >= PAGE_SIZE
              ? page * PAGE_SIZE + 1
              : list.length,
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pendamping data:", error);
      setError("Gagal memuat data pendamping. Silakan coba lagi.");
      setLoading(false);
    }
  };

  const handleFilterStatusChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const showPagination = totalRecords > PAGE_SIZE || currentPage > 1;

  const renderPagination = (position = "bottom") => {
    if (!showPagination) return null;

    const marginClass = position === "top" ? "mb-3" : "mt-3";
    const pageItems = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(
        (p) =>
          p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2,
      )
      .reduce((acc, p, i, arr) => {
        if (i > 0 && p - arr[i - 1] > 1) acc.push("ellipsis");
        acc.push(p);
        return acc;
      }, []);

    return (
      <div
        className={`d-flex justify-content-between align-items-center flex-wrap gap-2 ${marginClass}`}
      >
        <small className="text-muted">
          Halaman <strong>{currentPage}</strong> dari{" "}
          <strong>{totalPages.toLocaleString("id-ID")}</strong>
          {totalRecords > 0 && (
            <>
              {" "}
              ({totalRecords.toLocaleString("id-ID")} data)
            </>
          )}
        </small>
        <Pagination className="mb-0 flex-wrap">
          <Pagination.Prev
            disabled={currentPage <= 1 || loading}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          />
          {pageItems.map((item, idx) =>
            item === "ellipsis" ? (
              <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
            ) : (
              <Pagination.Item
                key={item}
                active={item === currentPage}
                disabled={loading}
                onClick={() => setCurrentPage(item)}
              >
                {item}
              </Pagination.Item>
            ),
          )}
          <Pagination.Next
            disabled={currentPage >= totalPages || loading}
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages, p + 1))
            }
          />
        </Pagination>
      </div>
    );
  };

  const handleShowDetail = (pendamping) => {
    setSelectedPendamping(pendamping);
    setReferralForm({
      kode_referral: pendamping.kode_referral || "",
      referral: pendamping.referral || "",
    });
    setReferralMessage(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPendamping(null);
    setReferralMessage(null);
    setReferralSaving(false);
  };

  const patchPendampingInLists = (id, patch) => {
    const apply = (list) =>
      list.map((item) =>
        item.id_pendamping === id ? { ...item, ...patch } : item,
      );
    setPendampingList((prev) => apply(prev));
    setFilteredList((prev) => apply(prev));
    setSelectedPendamping((prev) =>
      prev?.id_pendamping === id ? { ...prev, ...patch } : prev,
    );
  };

  const handleSaveReferral = async () => {
    if (!selectedPendamping?.id_pendamping) return;

    setReferralSaving(true);
    setReferralMessage(null);

    try {
      const response = await updatePendampingReferral(
        selectedPendamping.id_pendamping,
        {
          kode_referral: referralForm.kode_referral.trim() || null,
          referral: referralForm.referral.trim() || null,
          pendampingData: selectedPendamping,
        },
      );

      if (response.data.success) {
        const updated = response.data.data || {};
        patchPendampingInLists(selectedPendamping.id_pendamping, {
          kode_referral: updated.kode_referral,
          referral: updated.referral,
          nama_referrer: updated.nama_referrer,
          ...(response.data.autoSynced ? { sinkron: true } : {}),
        });
        setReferralForm({
          kode_referral: updated.kode_referral || "",
          referral: updated.referral || "",
        });
        setReferralMessage({ type: "success", text: response.data.message });
      }
    } catch (err) {
      setReferralMessage({
        type: "danger",
        text:
          err.response?.data?.message || "Gagal menyimpan referral.",
      });
    } finally {
      setReferralSaving(false);
    }
  };

  const handleSyncToLocal = async () => {
    try {
      setSyncLoading(true);
      setSyncMessage(null);

      // Kirim data yang sedang ditampilkan (hasil filter dan search)
      const response = await api.post("/pendamping/sync-external", {
        pendampingData: filteredList,
      });

      if (response.data.success) {
        // Update status sinkron untuk semua data yang di-sync
        const syncedIds = filteredList.map((item) => item.id_pendamping);

        const updateSyncStatus = (list) =>
          list.map((item) =>
            syncedIds.includes(item.id_pendamping)
              ? { ...item, sinkron: true }
              : item,
          );

        setPendampingList((prev) => updateSyncStatus(prev));
        setFilteredList((prev) => updateSyncStatus(prev));

        setSyncMessage({
          type: "success",
          text: `Sinkronisasi berhasil! ${response.data.summary.inserted} data ditambahkan, ${response.data.summary.updated} data diupdate${response.data.summary.errors > 0 ? `, ${response.data.summary.errors} error` : ""}.`,
        });
        // Auto dismiss after 5 seconds
        setTimeout(() => setSyncMessage(null), 5000);
      }
      setSyncLoading(false);
    } catch (error) {
      console.error("Error syncing data:", error);
      setSyncMessage({
        type: "danger",
        text: "Gagal melakukan sinkronisasi. Silakan coba lagi.",
      });
      setSyncLoading(false);
      // Auto dismiss after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const handleSyncSingleRow = async (pendamping) => {
    try {
      // Set loading state untuk baris ini
      setSyncingRows((prev) => ({ ...prev, [pendamping.id_pendamping]: true }));

      // Debug: log data yang akan dikirim
      console.log("Sending data to sync:", pendamping);

      // Kirim hanya 1 data sebagai array
      const response = await api.post("/pendamping/sync-external", {
        pendampingData: [pendamping],
      });

      if (response.data.success) {
        // Update state lokal untuk mengubah status sinkron
        const updateSyncStatus = (list) =>
          list.map((item) =>
            item.id_pendamping === pendamping.id_pendamping
              ? { ...item, sinkron: true }
              : item,
          );

        setPendampingList((prev) => updateSyncStatus(prev));
        setFilteredList((prev) => updateSyncStatus(prev));

        setSyncMessage({
          type: "success",
          text: `${pendamping.nama} berhasil disinkronkan! ${response.data.summary.inserted > 0 ? "Data ditambahkan" : "Data diupdate"}.`,
        });
        // Auto dismiss after 3 seconds
        setTimeout(() => setSyncMessage(null), 3000);
      }

      // Remove loading state untuk baris ini
      setSyncingRows((prev) => {
        const newState = { ...prev };
        delete newState[pendamping.id_pendamping];
        return newState;
      });
    } catch (error) {
      console.error("Error syncing single row:", error);
      setSyncMessage({
        type: "danger",
        text: `Gagal sinkronisasi ${pendamping.nama}. Silakan coba lagi.`,
      });
      // Remove loading state untuk baris ini
      setSyncingRows((prev) => {
        const newState = { ...prev };
        delete newState[pendamping.id_pendamping];
        return newState;
      });
      // Auto dismiss after 3 seconds
      setTimeout(() => setSyncMessage(null), 3000);
    }
  };

  const handleShowEmailModal = async (pendamping) => {
    setEmailTarget(pendamping);
    setPtspData({ username: "", email: "", phone_no: "" });
    setEmailResult(null);
    setShowEmailModal(true);
    try {
      setEmailFetchLoading(true);
      const response = await api.get(
        `/pendamping/ptsp/${pendamping.id_pendamping}`,
      );
      const d = response.data?.data || {};
      setPtspData({
        username: d.username || "",
        email: d.email || "",
        phone_no: d.phone_no || "",
      });
    } catch (err) {
      setEmailResult({
        type: "danger",
        text:
          err.response?.data?.message ||
          "Gagal mengambil data dari server SIHALAL.",
      });
    } finally {
      setEmailFetchLoading(false);
    }
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailTarget(null);
    setPtspData({ username: "", email: "", phone_no: "" });
    setEmailResult(null);
    setEmailLoading(false);
  };

  const handleUpdateEmail = async () => {
    if (!ptspData.username || !ptspData.email || !ptspData.phone_no) {
      setEmailResult({
        type: "danger",
        text: "Username, email, dan no HP tidak boleh kosong.",
      });
      return;
    }
    try {
      setEmailLoading(true);
      setEmailResult(null);
      const response = await api.post("/pendamping/otp-setting", {
        id: emailTarget.id_pendamping,
        ...ptspData,
      });
      setEmailResult({
        type:
          response.data?.status === "success" || response.data?.success
            ? "success"
            : "danger",
        text:
          response.data?.message ||
          (response.data?.status === "success"
            ? "Data berhasil diperbarui."
            : "Gagal memperbarui data."),
      });
    } catch (err) {
      setEmailResult({
        type: "danger",
        text: err.response?.data?.message || "Gagal memperbarui data.",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleShowResetModal = (pendamping) => {
    setResetTarget(pendamping);
    setNewPassword("");
    setResetResult(null);
    setShowResetModal(true);
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetTarget(null);
    setNewPassword("");
    setResetResult(null);
    setResetLoading(null);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setResetResult({ type: "danger", text: "Password minimal 6 karakter." });
      return;
    }
    try {
      setResetLoading("all");
      setResetResult(null);
      const [res1, res2] = await Promise.allSettled([
        api.post(
          "/pendamping/reset-password-unik-id",
          {
            id_pendamping: resetTarget.id_pendamping,
            new_password: newPassword,
          },
          { timeout: 15000 },
        ),
        api.post(
          "/pendamping/reset-password-by-id",
          {
            id_pendamping: resetTarget.id_pendamping,
            new_password: newPassword,
          },
          { timeout: 15000 },
        ),
      ]);
      const ok1 = res1.status === "fulfilled" && res1.value.data.success;
      const ok2 = res2.status === "fulfilled" && res2.value.data.success;
      if (ok1 || ok2) {
        setResetResult({ type: "success", text: "Password berhasil direset." });
      } else {
        const msg1 =
          res1.reason?.response?.data?.message ||
          res1.value?.data?.message ||
          "unik_id gagal";
        const msg2 =
          res2.reason?.response?.data?.message ||
          res2.value?.data?.message ||
          "id (PG) gagal";
        setResetResult({ type: "danger", text: `Gagal: ${msg1} | ${msg2}` });
      }
    } catch (err) {
      setResetResult({
        type: "danger",
        text: "Terjadi kesalahan saat mereset password.",
      });
    } finally {
      setResetLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      DISETUJUI: { variant: "success", text: "Disetujui" },
      "NON AKTIF": { variant: "secondary", text: "Non Aktif" },
      DIBLOKIR: { variant: "danger", text: "Diblokir" },
      DICABUT: { variant: "warning", text: "Dicabut" },
    };
    const statusInfo = statusMap[status] || {
      variant: "secondary",
      text: status,
    };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getFileUrl = (folder, filename, fullUrl) =>
    resolvePendampingDocUrl(folder, filename, fullUrl);

  const buildAbsoluteUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const buildKartuOpts = (p) => {
    const fotoPath = p?.filefoto ? getPendampingFileUrl("foto", p.filefoto) : "";
    const kabupatenNama =
      p?.namakabupaten || p?.kabupaten_nama || "";
    const provinsiNama =
      p?.namaprovinsi || p?.provinsi_nama || "";
    return {
      nama: p?.nama,
      nik: p?.nik,
      status: p?.status,
      email: p?.email,
      noHp: p?.no_hp,
      tglLahir: p?.tgl_lahir,
      kodeReferral: p?.kode_referral,
      noRegister: p?.no_register,
      idPendamping: p?.id_pendamping || p?.id,
      fotoUrl: fotoPath,
      logoUrl: logoUrl ? buildAbsoluteUrl(logoUrl) : "",
      orgName: orgName || "LP3H",
      orgAlamat,
      orgTelepon,
      primaryColor: themeColor,
      primaryDark: themeDark,
      primaryLight: themeLight,
      kabupatenNama,
      provinsiNama,
    };
  };

  const handleCetakKartu = async (format = KARTU_FORMAT.HORIZONTAL) => {
    if (!selectedPendamping) return;
    setKartuFormat(format);
    setKartuLoading(true);
    setKartuError(null);
    try {
      const dataUrl = await generateKartuPengenalImage(
        buildKartuOpts(selectedPendamping),
        format,
      );
      setKartuPreviewUrl(dataUrl);
      setShowKartuModal(true);
    } catch (err) {
      console.error("Generate kartu error:", err);
      setKartuError(err.message || "Gagal membuat kartu pengenal.");
    } finally {
      setKartuLoading(false);
    }
  };

  const handleSwitchKartuFormat = async (format) => {
    if (!selectedPendamping || format === kartuFormat) return;
    setKartuFormat(format);
    setKartuLoading(true);
    try {
      const dataUrl = await generateKartuPengenalImage(
        buildKartuOpts(selectedPendamping),
        format,
      );
      setKartuPreviewUrl(dataUrl);
    } catch (err) {
      setKartuError(err.message || "Gagal mengubah format kartu.");
    } finally {
      setKartuLoading(false);
    }
  };

  const handleDownloadKartu = () => {
    if (!kartuPreviewUrl || !selectedPendamping) return;
    const safeNik =
      (selectedPendamping.nik || "peserta").replace(/\D/g, "") || "peserta";
    const suffix =
      kartuFormat === KARTU_FORMAT.VERTICAL ? "gantungan" : "landscape";
    downloadDataUrl(kartuPreviewUrl, `kartu-pengenal-${suffix}-${safeNik}.png`);
  };

  const handlePrintKartu = () => {
    if (!kartuPreviewUrl) return;
    try {
      printDataUrl(
        kartuPreviewUrl,
        `Kartu Pengenal - ${selectedPendamping?.nama || ""}`,
        kartuFormat,
      );
    } catch (err) {
      setKartuError(err.message || "Gagal membuka dialog cetak.");
    }
  };

  return (
    <div>
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <div>
              <h1 className="page-header-title">Daftar Pendamping</h1>
              <p className="page-header-subtitle">
                Kelola dan monitoring data pendamping LP3H
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {/* Search and Filter Section */}
          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Control
                  type="search"
                  placeholder="Cari berdasarkan nama, email, no HP, atau NIK..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                  name="search-pendamping"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Select
                value={filterStatus}
                onChange={handleFilterStatusChange}
              >
                <option value="ALL">Semua Status</option>
                <option value="DISETUJUI">Disetujui</option>
                <option value="NON AKTIF">Non Aktif</option>
                <option value="DIBLOKIR">Diblokir</option>
                <option value="DICABUT">Dicabut</option>
              </Form.Select>
            </div>
            <div className="col-md-4 text-end">
              <Button
                variant="primary"
                onClick={() =>
                  fetchPendampingData(filterStatus, currentPage, debouncedSearch)
                }
                className="me-2"
              >
                <i className="fas fa-sync-alt me-2"></i>Refresh
              </Button>
              <Button
                variant="success"
                onClick={handleSyncToLocal}
                disabled={syncLoading}
              >
                {syncLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Syncing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-database me-2"></i>Update DB Lokal
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Sync Message Alert */}
          {syncMessage && (
            <Alert
              variant={syncMessage.type}
              dismissible
              onClose={() => setSyncMessage(null)}
              className="mt-3"
            >
              {syncMessage.text}
            </Alert>
          )}

          {!loading && renderPagination("top")}

          {/* Table Section */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Memuat data...</p>
            </div>
          ) : filteredList.length === 0 && !showPagination ? (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">Tidak ada data pendamping</h5>
              <p className="text-muted">
                {searchTerm || filterStatus !== "ALL"
                  ? "Tidak ada data yang sesuai dengan kriteria pencarian"
                  : "Belum ada pendamping yang terdaftar"}
              </p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">
                Tidak ada data pada halaman ini
                {debouncedSearch ? ` untuk "${debouncedSearch}"` : ""}.
              </p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                  Menampilkan{" "}
                  <strong>
                    {(currentPage - 1) * PAGE_SIZE + 1}–
                    {Math.min(currentPage * PAGE_SIZE, totalRecords)}
                  </strong>{" "}
                  dari <strong>{totalRecords.toLocaleString("id-ID")}</strong>{" "}
                  data
                  {debouncedSearch ? ` (pencarian: "${debouncedSearch}")` : ""}
                </div>
              </div>
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>No Register</th>
                      <th>Nama</th>
                      <th>Kode Referral</th>
                      <th>Referrer</th>
                      <th>Email</th>
                      <th>No HP</th>
                      <th>Status</th>
                      <th>Tanggal Daftar</th>
                      <th>Aksi</th>
                      <th>Password / Email</th>
                      <th>Status Sinkron</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((pendamping, index) => (
                      <tr key={pendamping.id_pendamping}>
                        <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                        <td>{pendamping.no_register || "-"}</td>
                        <td>{pendamping.nama}</td>
                        <td>
                          {pendamping.kode_referral ? (
                            <code>{pendamping.kode_referral}</code>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {pendamping.referral || pendamping.nama_referrer ? (
                            <div>
                              {pendamping.referral && (
                                <code className="me-2">
                                  {pendamping.referral}
                                </code>
                              )}
                              {pendamping.nama_referrer && (
                                <span>
                                  <i className="fas fa-user-tag me-1 text-primary"></i>
                                  {pendamping.nama_referrer}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{pendamping.email}</td>
                        <td>{pendamping.no_hp}</td>
                        <td>{getStatusBadge(pendamping.status)}</td>
                        <td>{formatDate(pendamping.created_on)}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleShowDetail(pendamping)}
                          >
                            <i className="fas fa-eye me-1"></i>Detail
                          </Button>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => handleShowResetModal(pendamping)}
                            >
                              <i className="fas fa-key me-1"></i>Reset Password
                            </Button>
                            <Button
                              type="button"
                              variant="warning"
                              size="sm"
                              onClick={() => handleShowEmailModal(pendamping)}
                            >
                              <i className="fas fa-envelope me-1"></i>Ganti
                              Email
                            </Button>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-center gap-2">
                            {pendamping.sinkron ? (
                              <Badge bg="success">
                                <i className="fas fa-check-circle me-1"></i>
                                Tersinkron
                              </Badge>
                            ) : (
                              <Badge bg="secondary">
                                <i className="fas fa-times-circle me-1"></i>
                                Belum Sinkron
                              </Badge>
                            )}
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleSyncSingleRow(pendamping)}
                              disabled={syncingRows[pendamping.id_pendamping]}
                            >
                              {syncingRows[pendamping.id_pendamping] ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-sync me-1"></i>Sinkron
                                </>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
          {!loading && renderPagination("bottom")}
        </Card.Body>
      </Card>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detail Pendamping</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPendamping && (
            <div>
              <div className="row mb-3">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Informasi Pribadi</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Nama Lengkap:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.nama}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>NIK:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.nik}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Tempat, Tanggal Lahir:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.tempat_lahir},{" "}
                  {formatDate(selectedPendamping.tgl_lahir)}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Alamat:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.alamat}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Kelurahan/Desa:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.kelurahan}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Kecamatan:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.kecamatan}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Kabupaten:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.namakabupaten ||
                    selectedPendamping.kabupaten_nama ||
                    selectedPendamping.kabupaten ||
                    "-"}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Provinsi:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.namaprovinsi ||
                    selectedPendamping.provinsi_nama ||
                    selectedPendamping.provinsi ||
                    "-"}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Kode Pos:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.kode_pos}</div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Kontak</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>No HP:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.no_hp}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Email:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.email}</div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Pendidikan & Pekerjaan</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Pendidikan Terakhir:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.pendidikan}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Program Studi:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.programstudi}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Universitas:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.universitas}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Pekerjaan:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.pekerjaan}
                  {selectedPendamping.pekerjaan_lain &&
                    ` (${selectedPendamping.pekerjaan_lain})`}
                </div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Informasi Rekening</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Bank:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.kode_bank}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>No Rekening:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.no_rekening}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Nama Rekening:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.nama_rekening}
                </div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Informasi Referral</h5>
                </div>
              </div>
              {referralMessage && (
                <Alert
                  variant={referralMessage.type}
                  dismissible
                  onClose={() => setReferralMessage(null)}
                  className="py-2"
                >
                  {referralMessage.text}
                </Alert>
              )}
              <div className="row mb-3">
                <div className="col-md-4">
                  <Form.Label>
                    <strong>Kode Referral</strong>
                  </Form.Label>
                </div>
                <div className="col-md-8">
                  <Form.Control
                    type="text"
                    value={referralForm.kode_referral}
                    onChange={(e) =>
                      setReferralForm((prev) => ({
                        ...prev,
                        kode_referral: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="Kode referral pendamping ini"
                    maxLength={50}
                  />
                  <Form.Text className="text-muted">
                    Kode unik yang dibagikan pendamping untuk referral orang
                    lain.
                  </Form.Text>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <Form.Label>
                    <strong>Referral Dari</strong>
                  </Form.Label>
                </div>
                <div className="col-md-8">
                  <Form.Control
                    type="text"
                    value={referralForm.referral}
                    onChange={(e) =>
                      setReferralForm((prev) => ({
                        ...prev,
                        referral: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="Kode referral pemberi referral (kosongkan jika tidak ada)"
                    maxLength={50}
                  />
                  {selectedPendamping.nama_referrer && (
                    <Form.Text className="text-muted">
                      Referrer: {selectedPendamping.nama_referrer}
                    </Form.Text>
                  )}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-8 offset-md-4">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleSaveReferral}
                    disabled={referralSaving}
                  >
                    {referralSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Simpan Referral
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Alasan & Status</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Alasan Mendaftar:</strong>
                </div>
                <div className="col-md-8">{selectedPendamping.alasan}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Status:</strong>
                </div>
                <div className="col-md-8">
                  {getStatusBadge(selectedPendamping.status)}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Tanggal Daftar:</strong>
                </div>
                <div className="col-md-8">
                  {formatDate(selectedPendamping.created_on)}
                </div>
              </div>

              <div className="row mb-3 mt-4">
                <div className="col-md-12">
                  <h5 className="border-bottom pb-2">Dokumen</h5>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Foto Diri:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.filefoto ? (
                    <a
                      href={getFileUrl(
                        "foto",
                        selectedPendamping.filefoto,
                        selectedPendamping.filefoto_url,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-file-image me-2"></i>Lihat Foto
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Foto KTP:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.fotoktp ? (
                    <a
                      href={getFileUrl(
                        "ktp",
                        selectedPendamping.fotoktp,
                        selectedPendamping.fotoktp_url,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-file-image me-2"></i>Lihat File
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4">
                  <strong>Foto Ijazah:</strong>
                </div>
                <div className="col-md-8">
                  {selectedPendamping.fotoijazah ? (
                    <a
                      href={getFileUrl(
                        "ijazah",
                        selectedPendamping.fotoijazah,
                        selectedPendamping.fotoijazah_url,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-file-image me-2"></i>Lihat File
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => handleCetakKartu(KARTU_FORMAT.HORIZONTAL)}
            disabled={kartuLoading || !selectedPendamping}
          >
            {kartuLoading && kartuFormat === KARTU_FORMAT.HORIZONTAL ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Membuat...
              </>
            ) : (
              <>
                <i className="fas fa-id-card me-2"></i>
                Kartu Landscape
              </>
            )}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => handleCetakKartu(KARTU_FORMAT.VERTICAL)}
            disabled={kartuLoading || !selectedPendamping}
          >
            {kartuLoading && kartuFormat === KARTU_FORMAT.VERTICAL ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Membuat...
              </>
            ) : (
              <>
                <i className="fas fa-id-badge me-2"></i>
                Kartu Gantungan
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCloseModal}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showKartuModal}
        onHide={() => setShowKartuModal(false)}
        size="lg"
        centered
        className={`kartu-pengenal-modal${
          kartuFormat === KARTU_FORMAT.VERTICAL ? " kartu-pengenal-modal--vertical" : ""
        }`}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-id-card me-2"></i>
            Kartu Pengenal — {getKartuFormatLabel(kartuFormat)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {kartuError && (
            <Alert variant="danger" className="text-start">
              {kartuError}
            </Alert>
          )}
          {!selectedPendamping?.filefoto && (
            <Alert variant="warning" className="text-start">
              Foto diri belum tersedia. Kartu tetap bisa dicetak tanpa foto.
            </Alert>
          )}
          <div className="d-flex justify-content-center gap-2 mb-3 flex-wrap">
            <Button
              size="sm"
              variant={
                kartuFormat === KARTU_FORMAT.HORIZONTAL ? "primary" : "outline-primary"
              }
              onClick={() => handleSwitchKartuFormat(KARTU_FORMAT.HORIZONTAL)}
              disabled={kartuLoading}
            >
              Landscape
            </Button>
            <Button
              size="sm"
              variant={
                kartuFormat === KARTU_FORMAT.VERTICAL ? "primary" : "outline-primary"
              }
              onClick={() => handleSwitchKartuFormat(KARTU_FORMAT.VERTICAL)}
              disabled={kartuLoading}
            >
              Gantungan Leher
            </Button>
          </div>
          {kartuLoading ? (
            <Spinner animation="border" />
          ) : kartuPreviewUrl ? (
            <div className="kartu-pengenal-preview-wrap">
              <img
                src={kartuPreviewUrl}
                alt="Preview kartu pengenal"
                className="kartu-pengenal-preview-img"
              />
            </div>
          ) : null}
          <p className="text-muted small mt-3 mb-0">
            {kartuFormat === KARTU_FORMAT.VERTICAL
              ? "Ukuran gantungan leher (54 × 86 mm)."
              : "Ukuran kartu landscape (85,6 × 54 mm)."}
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-2 flex-wrap">
          <Button variant="primary" onClick={handleDownloadKartu} disabled={!kartuPreviewUrl}>
            <i className="fas fa-download me-2"></i>
            Download PNG
          </Button>
          <Button variant="outline-primary" onClick={handlePrintKartu} disabled={!kartuPreviewUrl}>
            <i className="fas fa-print me-2"></i>
            Cetak
          </Button>
          <Button variant="secondary" onClick={() => setShowKartuModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={handleCloseResetModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-key me-2"></i>Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetTarget && (
            <div>
              <div className="mb-3">
                <strong>Pendamping:</strong> {resetTarget.nama}
              </div>
              <div className="mb-3">
                <small className="text-muted">
                  ID: <code>{resetTarget.id_pendamping}</code>
                </small>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Password Baru</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Minimal 6 karakter"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={resetLoading !== null}
                  autoComplete="new-password"
                  name="new-password-pendamping"
                />
              </Form.Group>
              {resetResult && (
                <Alert variant={resetResult.type}>{resetResult.text}</Alert>
              )}
              <div className="d-grid">
                <Button
                  variant="primary"
                  onClick={handleResetPassword}
                  disabled={resetLoading !== null}
                >
                  {resetLoading === "all" ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Memproses...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>Update
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseResetModal}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Ganti Email Modal */}
      <Modal show={showEmailModal} onHide={handleCloseEmailModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-envelope me-2"></i>Ganti Email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {emailTarget && (
            <div>
              <div className="mb-3">
                <strong>Pendamping:</strong> {emailTarget.nama}
              </div>
              <div className="mb-3">
                <small className="text-muted">
                  ID: <code>{emailTarget.id_pendamping}</code>
                </small>
              </div>
              {emailFetchLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Memuat data dari SIHALAL...
                </div>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      value={ptspData.username}
                      onChange={(e) =>
                        setPtspData((p) => ({ ...p, username: e.target.value }))
                      }
                      disabled={emailLoading}
                      autoComplete="off"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Untuk OTP</Form.Label>
                    <Form.Control
                      type="email"
                      value={ptspData.email}
                      onChange={(e) =>
                        setPtspData((p) => ({ ...p, email: e.target.value }))
                      }
                      disabled={emailLoading}
                      autoComplete="off"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>No. HP Untuk OTP</Form.Label>
                    <Form.Control
                      type="text"
                      value={ptspData.phone_no}
                      onChange={(e) =>
                        setPtspData((p) => ({ ...p, phone_no: e.target.value }))
                      }
                      disabled={emailLoading}
                      autoComplete="off"
                    />
                  </Form.Group>
                  {emailResult && (
                    <Alert variant={emailResult.type}>{emailResult.text}</Alert>
                  )}
                  <div className="d-grid">
                    <Button
                      variant="primary"
                      onClick={handleUpdateEmail}
                      disabled={emailLoading}
                    >
                      {emailLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Memproses...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>Update
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEmailModal}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DaftarPendamping;
